import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Shield, X, LogOut, ChevronRight } from 'lucide-react';
import Icon from '../common/Icon';
import { useAppStore } from '../../store/useAppStore';

export interface NavItem {
  label: string;
  icon: string;
  to: string;
  badge?: number;
  children?: NavItem[];
}

interface Props {
  navItems: NavItem[];
  title: string;
  subtitle: string;
}

const PortalSidebar: React.FC<Props> = ({ navItems, title, subtitle }) => {
  const { sidebarOpen, setSidebarOpen, user, logout } = useAppStore();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (to: string) => location.pathname === to || location.pathname.startsWith(to + '/');

  if (!sidebarOpen) return null;

  return (
    <>
      {/* Overlay on mobile */}
      <div
        onClick={() => setSidebarOpen(false)}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 40, display: 'none' }}
        className="sidebar-overlay"
      />

      <aside style={{
        position: 'fixed', top: 0, left: 0, height: '100vh', width: '260px',
        background: 'var(--sidebar-bg)', borderRight: '1px solid rgba(255,255,255,0.05)',
        display: 'flex', flexDirection: 'column', zIndex: 50,
        boxShadow: '4px 0 24px rgba(0,0,0,0.2)',
        transition: 'transform 0.3s ease',
      }}>
        {/* Logo */}
        <div style={{ padding: '1.25rem 1.25rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', textDecoration: 'none' }}>
              <div style={{ width: '2.25rem', height: '2.25rem', borderRadius: '0.625rem', background: 'linear-gradient(135deg, #f97316, #ea580c)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Shield size={16} color="white" strokeWidth={2.5} />
              </div>
              <div>
                <div style={{ color: 'white', fontWeight: 800, fontSize: '0.9rem', lineHeight: 1.1 }}>Cates Roofing</div>
                <div style={{ color: '#f97316', fontSize: '0.6rem', fontWeight: 500, letterSpacing: '0.04em' }}>{subtitle.toUpperCase()}</div>
              </div>
            </Link>
            <button onClick={() => setSidebarOpen(false)} className="sidebar-close-btn"
              style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '0.25rem' }}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* User Info */}
        {user && (
          <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} style={{ width: '2.25rem', height: '2.25rem', borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '2.25rem', height: '2.25rem', borderRadius: '50%', background: 'linear-gradient(135deg, #f97316, #ea580c)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '0.85rem' }}>
                  {user.name.charAt(0)}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: 'white', fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.name}</div>
                <div style={{ color: '#64748b', fontSize: '0.7rem', textTransform: 'capitalize' }}>{user.role}</div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 0.75rem', scrollbarWidth: 'thin' }}>
          {navItems.map((item) => (
            <div key={item.to} style={{ marginBottom: '0.25rem' }}>
              <Link
                to={item.to}
                className={`sidebar-link ${isActive(item.to) ? 'active' : ''}`}
                style={{ justifyContent: 'space-between' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Icon name={item.icon} size={17} />
                  <span>{item.label}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span style={{
                      background: '#f97316', color: 'white', borderRadius: '9999px',
                      fontSize: '0.65rem', fontWeight: 700, padding: '0.125rem 0.375rem', minWidth: '1.2rem', textAlign: 'center',
                    }}>{item.badge}</span>
                  )}
                  {item.children && <ChevronRight size={13} />}
                </div>
              </Link>
            </div>
          ))}
        </nav>

        {/* Bottom */}
        <div style={{ padding: '1rem 0.75rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.625rem 1rem', borderRadius: '0.625rem', fontSize: '0.8rem', color: '#64748b', textDecoration: 'none', marginBottom: '0.375rem' }}>
            <Icon name="Globe" size={15} />
            <span>View Public Site</span>
          </Link>
          <button onClick={handleLogout} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.625rem 1rem', borderRadius: '0.625rem', fontSize: '0.8rem', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', transition: 'background 0.15s' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgb(239 68 68 / 0.1)') }
            onMouseLeave={e => (e.currentTarget.style.background = 'none') }>
            <LogOut size={15} /> Sign Out
          </button>
        </div>
      </aside>
      <style>{`
        @media (max-width: 768px) { .sidebar-overlay { display: block !important; } }
      `}</style>
    </>
  );
};

export default PortalSidebar;
